/* eslint-disable guard-for-in */

import map from 'lodash/map';
import Immutable from 'seamless-immutable';

import {
	INITIALIZE_DATAKEY,
	RESET_ALL_DIRTY_STATES,
	RESET_DIRTY_STATE,
	SET_ALL_DIRTY_STATES_BLURRED,
	SET_BLURRED,
	SET_FOCUSED,
	SET_MODEL,
	SET_MODEL_PROPERTY,
} from '../actions/types';
import { DirtyState, DirtyStates, Model } from '../types';

export const initialState = Immutable<{
	[dataKey: string]: DirtyStates<string> | undefined;
}>({});

const defaultDirtyState: DirtyState = {
	hasBlurred: false,
	isBlurred: false,
	hasFocused: false,
	isFocused: false,
	hasTouched: false,
	hasChanged: false,
};

const generateInitialStatus = <FieldNames extends string>(
	initialModel: Model<FieldNames>,
): Record<FieldNames, DirtyState | undefined> => {
	const output: Record<FieldNames, DirtyState | undefined> = {} as Record<FieldNames, DirtyState | undefined>;
	map(initialModel, (value, fieldName: FieldNames) => {
		output[fieldName] = defaultDirtyState;
	});
	return output;
};

const mergeDeep = (
	pre: Immutable.ImmutableObject<{
		[dataKey: string]: DirtyStates<string> | undefined;
	}>,
	post: {
		[dataKey: string]:
			| {
					[fieldName: string]: Partial<DirtyState> | undefined;
			  }
			| undefined;
	},
): Immutable.ImmutableObject<{
	[dataKey: string]: DirtyStates<string> | undefined;
}> => pre.merge(post, { deep: true });

const dirtyStateReducer = (
	state = initialState,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	action: { [key: string]: any },
): Immutable.ImmutableObject<{
	[dataKey: string]: DirtyStates<string> | undefined;
}> => {
	const { dataKey, initialModel } = action.payload || {};
	switch (action.type) {
		case INITIALIZE_DATAKEY: {
			const status = generateInitialStatus(initialModel);
			return state.setIn([dataKey], status);
		}

		case SET_MODEL: {
			const { changedFields = [] } = action.payload;
			for (const fieldName in state[dataKey]) {
				const hasChanged = changedFields.indexOf(fieldName) !== -1;
				state = state.setIn([dataKey, fieldName, 'hasTouched'], true);
				state = state.setIn([dataKey, fieldName, 'hasChanged'], hasChanged);
			}
			return state;
		}

		case SET_MODEL_PROPERTY: {
			const { hasChanged = false, name } = action.payload;
			return mergeDeep(state, {
				[dataKey]: {
					[name]: {
						hasChanged,
						hasTouched: true,
					},
				},
			});
		}

		case SET_BLURRED: {
			const { name } = action.payload;
			return mergeDeep(state, {
				[dataKey]: {
					[name]: {
						hasBlurred: true,
						isBlurred: true,
						isFocused: false,
					},
				},
			});
		}

		case SET_FOCUSED: {
			const { name } = action.payload;
			return mergeDeep(state, {
				[dataKey]: {
					[name]: {
						isBlurred: false,
						hasFocused: true,
						isFocused: true,
					},
				},
			});
		}

		case RESET_DIRTY_STATE: {
			const { name } = action.payload;
			return mergeDeep(state, {
				[dataKey]: {
					[name]: defaultDirtyState,
				},
			});
		}

		case RESET_ALL_DIRTY_STATES: {
			const currentModel = state[dataKey];
			let updatedState = state;
			map(currentModel, (value, fieldName) => {
				updatedState = mergeDeep(updatedState, {
					[dataKey]: {
						[fieldName]: defaultDirtyState,
					},
				});
			});
			return updatedState;
		}

		case SET_ALL_DIRTY_STATES_BLURRED: {
			for (const fieldName in state[dataKey]) {
				state = state.setIn([dataKey, fieldName, 'hasBlurred'], true);
			}
			return state;
		}

		default:
			return state;
	}
};

export default dirtyStateReducer;
